<template>
  <div>
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center">
        <wz-button variant="light" class="mr-3" @click="$router.push({ name: 'operational-payment-list' })">
          <i class="ri-arrow-left-line mr-1"></i>Volver
        </wz-button>
        <h1 class="wz-page-title mb-0">Lote de pago operativo {{ batch ? '#' + batch.id : '' }}</h1>
      </div>
      <wz-badge v-if="batch" :variant="statusVariant(batch.status)" pill>{{ batch.status }}</wz-badge>
    </div>

    <template v-if="batch">
      <wz-card title="Datos del lote" class="mb-3">
        <div class="flex flex-wrap -mx-2">
          <div class="w-full md:w-1/3 px-2 mb-3"><small class="text-muted block">Empresa</small><strong>{{ batch.companyName }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3"><small class="text-muted block">Concepto</small><strong>{{ batch.conceptName }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3"><small class="text-muted block">Período</small><strong>{{ period }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3"><small class="text-muted block">Total (S/)</small><strong>{{ formatAmount(batch.totalAmount) }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3"><small class="text-muted block">Registros</small><strong>{{ batch.totalRecords || 0 }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3" v-if="batch.description"><small class="text-muted block">Descripción</small><strong>{{ batch.description }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3" v-if="batch.approvedBy"><small class="text-muted block">Aprobado por</small><strong>{{ batch.approvedBy }}</strong></div>
          <div class="w-full md:w-1/3 px-2 mb-3" v-if="batch.paidBy"><small class="text-muted block">Pagado por</small><strong>{{ batch.paidBy }}</strong></div>
          <div class="w-full px-2 mb-3" v-if="batch.status === 'ANULADO'">
            <small class="text-muted block">Motivo de anulación</small><strong>{{ batch.annulledReason }}</strong>
          </div>
        </div>

        <hr class="my-3" />
        <div class="flex flex-wrap gap-2">
          <wz-button v-if="isPending" variant="outline" size="sm" @click="downloadTemplate"><i class="ri-download-2-line mr-1"></i>Plantilla</wz-button>
          <wz-button v-if="isPending" variant="primary" size="sm" @click="openUpload"><i class="ri-upload-2-line mr-1"></i>Subir Excel</wz-button>
          <wz-button v-if="isPending && hasDetails" variant="outline" size="sm" @click="approve"><i class="ri-check-line mr-1"></i>Aprobar</wz-button>
          <wz-button v-if="canPay" variant="success" size="sm" @click="pay"><i class="ri-money-dollar-circle-line mr-1"></i>Marcar pagado</wz-button>
          <wz-button v-if="canAnnul" variant="outline-danger" size="sm" @click="modalAnnul = true"><i class="ri-close-circle-line mr-1"></i>Anular</wz-button>
          <wz-button v-if="hasDetails" variant="outline" size="sm" @click="exportDetail"><i class="ri-file-excel-2-line mr-1"></i>Exportar</wz-button>
        </div>
      </wz-card>

      <wz-card title="Detalle de pagos" class="mb-3">
        <div v-if="!hasDetails" class="text-center text-muted py-4">Aún no hay pagos cargados. Suba un Excel.</div>
        <div v-else class="overflow-x-auto">
          <table class="wz-table w-full">
            <thead>
              <tr><th>DNI</th><th>Nombre completo</th><th class="text-right">Monto (S/)</th><th>Observación</th><th>Estado</th></tr>
            </thead>
            <tbody>
              <tr v-for="d in batch.details" :key="d.id">
                <td>{{ d.dni }}</td>
                <td>{{ d.employeeFullName }}</td>
                <td class="text-right">{{ formatAmount(d.amount) }}</td>
                <td>{{ d.observation || '—' }}</td>
                <td><wz-badge :variant="d.status === 'PAGADO' ? 'success-solid' : 'warning-solid'" pill>{{ d.status }}</wz-badge></td>
              </tr>
            </tbody>
          </table>
        </div>
      </wz-card>
    </template>

    <!-- Modal subir Excel + preview -->
    <wz-modal v-model="modalUpload" title="Subir Excel de pagos" size="lg">
      <div class="mb-3">
        <label class="font-medium">Archivo .xlsx <small class="text-muted">(columnas: DNI | Monto | Observación)</small></label>
        <wz-file-input :key="fileKey" id-file="op-upload" :accept-file="['.xlsx']" @onChange="onFileChange" />
      </div>
      <div v-if="preview" class="mb-2">
        <span class="mr-3"><strong>{{ preview.validRows }}</strong> válidas</span>
        <span class="text-danger"><strong>{{ preview.errorRows }}</strong> con error</span>
      </div>
      <div v-if="preview" class="overflow-x-auto" style="max-height: 320px;">
        <table class="wz-table w-full">
          <thead><tr><th>Fila</th><th>DNI</th><th>Monto</th><th>Observación</th><th>Resultado</th></tr></thead>
          <tbody>
            <tr v-for="r in preview.rows" :key="r.rowNumber" :style="r.valid ? 'background:rgba(16,185,129,0.08)' : 'background:rgba(239,68,68,0.08)'">
              <td>{{ r.rowNumber }}</td>
              <td>{{ r.dni }}</td>
              <td>{{ r.amount }}</td>
              <td>{{ r.observation || '—' }}</td>
              <td>
                <span v-if="r.valid" class="text-success"><i class="ri-check-line"></i> OK</span>
                <span v-else class="text-danger">{{ r.errors.join(' | ') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalUpload = false">Cancelar</wz-button>
        <wz-button v-if="preview && preview.validRows > 0" variant="primary" :disabled="confirming" @click="confirm">
          {{ confirming ? 'Confirmando...' : ('Confirmar ' + preview.validRows + ' pago(s)') }}
        </wz-button>
      </template>
    </wz-modal>

    <!-- Modal anular -->
    <wz-modal v-model="modalAnnul" title="Anular lote">
      <p class="mb-2">Esta acción marca el lote como ANULADO. Indique el motivo (mínimo 10 caracteres).</p>
      <wz-textarea v-model="annulReason" :rows="3" maxlength="500" placeholder="Motivo de anulación" />
      <template #footer>
        <wz-button variant="outline-secondary" @click="modalAnnul = false">Cancelar</wz-button>
        <wz-button variant="danger" :disabled="(annulReason || '').trim().length < 10" @click="annul">Anular</wz-button>
      </template>
    </wz-modal>
  </div>
</template>

<script>
import { Alert } from '@wizepe/wizeui'
import { OperationalPaymentService } from '@/core/services'
import fileDownload from 'js-file-download'

export default {
  data() {
    return {
      batch: null,
      modalUpload: false,
      modalAnnul: false,
      annulReason: '',
      file: null,
      fileKey: 0,
      preview: null,
      confirming: false,
    }
  },
  computed: {
    period() { return this.batch ? String(this.batch.periodMonth).padStart(2, '0') + '/' + this.batch.periodYear : '' },
    hasDetails() { return this.batch && this.batch.details && this.batch.details.length > 0 },
    isPending() { return this.batch && this.batch.status === 'PENDIENTE' },
    canPay() { return this.batch && ['PENDIENTE', 'APROBADO'].includes(this.batch.status) && this.hasDetails },
    canAnnul() { return this.batch && ['PENDIENTE', 'APROBADO'].includes(this.batch.status) },
  },
  methods: {
    statusVariant(s) {
      return { PENDIENTE: 'warning-solid', APROBADO: 'info-solid', PAGADO: 'success-solid', ANULADO: 'danger-solid' }[s] || 'dark'
    },
    formatAmount(v) {
      if (v == null) return '0.00'
      return Number(v).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async load() {
      try {
        await this.$store.dispatch('app/loading', true)
        this.batch = (await OperationalPaymentService.getBatch(this.$route.params.id)).payload
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al cargar el lote')
        this.$router.push({ name: 'operational-payment-list' })
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    openUpload() {
      this.file = null
      this.preview = null
      this.fileKey += 1
      this.modalUpload = true
    },
    onFileChange(files) {
      this.file = files && files.length ? files[0] : null
      if (this.file) this.doUpload()
    },
    async doUpload() {
      try {
        await this.$store.dispatch('app/loading', true)
        this.preview = (await OperationalPaymentService.upload(this.batch.id, this.file)).payload
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al validar el archivo')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async confirm() {
      try {
        this.confirming = true
        await OperationalPaymentService.confirm(this.batch.id)
        Alert.success('Pagos confirmados')
        this.modalUpload = false
        await this.load()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al confirmar')
      } finally {
        this.confirming = false
      }
    },
    async approve() {
      await this.runAction(() => OperationalPaymentService.approve(this.batch.id), 'Lote aprobado')
    },
    async pay() {
      if (!confirm('¿Marcar todo el lote como PAGADO? Esta acción no se puede revertir.')) return
      await this.runAction(() => OperationalPaymentService.pay(this.batch.id), 'Lote marcado como pagado')
    },
    async annul() {
      try {
        await OperationalPaymentService.annul(this.batch.id, this.annulReason.trim())
        this.modalAnnul = false
        this.annulReason = ''
        Alert.success('Lote anulado')
        await this.load()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error al anular')
      }
    },
    async runAction(fn, okMsg) {
      try {
        await this.$store.dispatch('app/loading', true)
        await fn()
        Alert.success(okMsg)
        await this.load()
      } catch (e) {
        console.error(e)
        Alert.error(e.errors ? e.errors.message : 'Error en la operación')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async downloadTemplate() {
      try {
        const resp = await OperationalPaymentService.template(this.batch.id)
        fileDownload(resp.data, 'plantilla-pagos-operativos.xlsx')
      } catch (e) { Alert.error('Error al descargar la plantilla') }
    },
    async exportDetail() {
      try {
        const resp = await OperationalPaymentService.export(this.batch.id)
        fileDownload(resp.data, 'pagos-operativos-lote-' + this.batch.id + '.xlsx')
      } catch (e) { Alert.error('Error al exportar') }
    },
  },
  created() { this.load() },
}
</script>
